<script>
  import { onDestroy, createEventDispatcher } from 'svelte';

  import ComponentEvents from '@utils/component-events';
  import Notification from '@utils/notification';
  import BrowserUtils from '@utils/browser-utils';
  import Track from '@utils/track';
  import GroupAjax from '@ajax/group';

  import PreloadBox from '@components/PreloadBox.svelte';
  import Text from '@components/Text.svelte';

  import MobileGoToMapButton from '@app-components/group/MobileGoToMapButton.svelte';
  import ShareModal from '@app-components/modal/ShareModal.svelte';
  import DemoAccountNotification from '@app-components/DemoAccountNotification.svelte';
  import ListAuthors from '@app-components/ListAuthors.svelte';
  import WelcomeNoteActiveIndicator from '@app-components/WelcomeNoteActiveIndicator.svelte';
  import MobileSectionsBar from '@app-components/group/MobileSectionsBar.svelte';
  import EmbeddedPostCarousel from '@app-components/group/EmbeddedPostCarousel.svelte';
  import RecRequestModalMobile from '@app-components/mobile/RecRequestModal.svelte';
  import MobileSections from '@app-components/mobile/MobileSections.svelte';
  import ColumnSection from '@app-components/group/ColumnSection.svelte';
  import NoRecsFoundText from '@app-components/group/NoRecsFoundText.svelte';
  import FixedAddButton from '@app-components/group/FixedAddButton.svelte';
  import Quiz from './Quiz.svelte';
  import { browser } from '$app/environment';
  // exports
  export let group;
  export let loaded;
  export let recRequests;
  export let sections;
  export let userCanEdit;
  export let userCanContribute;
  export let recs;
  export let showShareModal;
  export let currentUser;
  export let isSignedInUser;
  export let brokenLinksCount = 0;
  export let loadSections;
  export let showAddComment = false;

  // claps
  let withQuiz = false;
  let quizExpanded = true;
  let quizInstance;

  // map view button
  let fixedSectionBarShown = false;
  let shouldShowMapViewButton = false;
  $: {
    if (!group.map_view_enabled) {
      shouldShowMapViewButton = false;
    } else if ((userCanEdit || userCanContribute) && !fixedSectionBarShown) {
      shouldShowMapViewButton = false;
    } else {
      shouldShowMapViewButton = true;
    }
  }
  let scrollPastRecId = null;
  $: {
    if (!userCanContribute) {
      let objectId = null;
      if (sections && sections.length > 0) {
        const sortOrders = sections.map(function (element) {
          return element.sort_order;
        });

        objectId = [].concat.apply([], sortOrders)[1];
      } else if (group && group.rec_objects_sort_order && group.rec_objects_sort_order.length > 0) {
        objectId = group.rec_objects_sort_order[1];
      }

      if (objectId) {
        scrollPastRecId = parseInt(objectId);
      }
    }
  }

  const unsubShowAndHideSectionBar = ComponentEvents.on('mobile-show-fixed-section-bar', ({ showSectionBar }) => {
    fixedSectionBarShown = showSectionBar;
  });
  // END map view button

  // updateGroup AJAX
  let updateGroup = async function (data) {
    try {
      let resp = await GroupAjax.update(group, data);
      group.set(resp);
    } catch (err) {
      if (err && err.full_errors && err.full_errors.length > 0) {
        Notification.send({
          text: err.full_errors[0],
        });
        group = group;
      }
    }
  };
  // END updateGroup AJAX

  // dispatches
  const dispatch = createEventDispatcher();

  const dispatchAddButtonClicked = (e) => {
    e.preventDefault();
    dispatch('add-button-click', {});
  };

  const dispatchMapViewButtonClicked = (e) => {
    e.preventDefault();
    dispatch('map-view-button-click');
  };
  const scrollToFirstBrokenLinkItem = function () {
    const elem = document.getElementsByClassName('broken-link-rec-object')[0];
    if (elem) {
      elem.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  // lifecycle events
  onDestroy(() => {
    unsubShowAndHideSectionBar();
  });
</script>

<style style="text/scss" lang="scss">
  .m-group-name {
    margin-top: 30px;
    font-weight: 600;
    font-size: 30px;
    line-height: 42px;
  }

  .m-authors {
    margin-top: 22px;
    margin-bottom: 5px;
    display: flex;
    flex-direction: row;
  }

  .toast-mobile {
    display: flex;
    flex-direction: row;
    align-items: flex-start;
    gap: 8px;
    background: #fff4f5;
    border: 1px solid #ff5567;
    padding: 18px 16px;
    border-radius: 12px;
    margin-top: 16px;
    cursor: pointer;
    .icon-alert {
      width: 16px;
      margin-top: 2px;
    }
    .title {
      font-weight: 700;
      font-size: 14px;
      line-height: 20px;
      color: #ff5567;
    }
    .description {
      font-size: 12px;
      line-height: 16px;
      color: #7f7f7f;
      margin-top: 4px;
    }
  }

  .m-description {
    display: flex;
    margin-top: 24px;
    font-size: 15px;
    line-height: 24px;
    opacity: 0.5;
  }

  .m-claps-container {
    position: fixed;
    bottom: 40px;
    left: 0px;
    padding: 0px 10px;
    z-index: 10;

    .m-clap-btn {
      border-radius: 15px;
      min-width: 78px;
      border: none;
      height: 48px;
      backdrop-filter: blur(10px);
      background-color: rgba(0, 0, 0, 0.05);
      color: #000 !important;
      touch-action: manipulation;
      padding-left: 15px;
      padding-right: 15px;
      display: flex;
      align-items: center;
      justify-content: center;
      padding-top: 0;
      padding-bottom: 0;
      font-weight: 500;

      .m-emoji {
        width: 24px;
        height: 24px;
        flex-shrink: 0;
        margin-top: -2px;
        display: inline-block;
        background: url(/images/icon__clapping.png) 50% no-repeat;
        background-size: cover;
        transition: all 0.2s ease-in-out;
      }

      :global(.emoji-active) {
        opacity: 0.2;
      }

      .m-claps-counter {
        margin-left: 12px;
        font-size: 12px;
        font-weight: 500;
      }

      &:hover {
        background-color: rgba(0, 0, 0, 0.1);
      }
    }
  }

  :global(.bottom-bar-visible) {
    .m-claps-container {
      bottom: 70px !important;
    }
  }
  :global(.group-name-h1) {
    font-size: 30px;
    font-weight: 600;
    margin-top: 0px;
    margin-bottom: 0px;
    margin-block-start: 0px;
    margin-block-end: 0px;
    line-height: 42px;
    overflow: hidden;
  }
</style>

<ShareModal mobile={true} bind:showShareModal={showShareModal} group={group} />
<DemoAccountNotification mobile={true} />

<div
  style="overflow-x: hidden; margin-left: -30px; margin-right: -30px;
      padding-left: 30px; padding-right: 30px; {userCanEdit ? 'user-select: none;' : ''}">
  <div class="m-group-name">
    {#if userCanEdit}
      <Text
        on:blur={(e) => {
          updateGroup({ name: e.detail.text });
        }}
        lines={10}
        text={group.name}
        expand={false}
        editable={true} />
    {:else}
      <h1 class="group-name-h1">
        <Text text={group.name} expand={false} editable={false} lines={10} />
      </h1>
    {/if}
  </div>
  <div class="m-authors">
    <ListAuthors group={group} authors={group.users.filter((u) => u.role != 'viewer')} mobile={true} />
    <WelcomeNoteActiveIndicator location="group" mobile={true} />
  </div>

  {#if brokenLinksCount > 0}
    <div
      class="toast-mobile"
      on:click|preventDefault|stopPropagation={() => {
        scrollToFirstBrokenLinkItem();
      }}>
      <img class="icon-alert" src="/images/alert.svg" alt="" />
      <div class="content">
        <div class="title">
          {#if brokenLinksCount > 1}
            {brokenLinksCount} items on this list have broken links!
          {:else}
            1 item on this list has a broken link!
          {/if}
        </div>
        <div class="description">
          You should consider fixing or removing these recommendations from your list, and we’ll hide all broken links
          from any viewers.
        </div>
      </div>
    </div>
  {/if}
  {#if group.description || userCanEdit}
    <div class="m-description" style={userCanEdit ? 'opacity: 0.75' : ''}>
      <Text
        on:blur={(e) => {
          updateGroup({ description: e.detail.text });
        }}
        linkClicked={(url) => {
          Track.groupDescriptionLinkClicked({
            user: currentUser,
            group: group,
            url: url,
          });
        }}
        text={group.description}
        editable={userCanEdit}
        expand={true}
        lines={4}
        placeholder={'Add description'} />
    </div>
  {/if}
  {#if browser}
    <EmbeddedPostCarousel mobile={true} user={currentUser} group={group} />
  {/if}

  {#if !loaded}
    <div style="height: 280px; margin-top: 40px; width: calc(100% + 40px); margin-left: -20px;">
      <PreloadBox />
    </div>
    <div style="height: 280px; margin-top: 20px; width: calc(100% + 40px); margin-left: -20px;">
      <PreloadBox />
    </div>
  {:else}
    <MobileSectionsBar />

    {#if recRequests && recRequests.length > 0}
      <RecRequestModalMobile />
    {/if}

    {#if group && sections && sections.length > 0}
      <MobileSections />
    {/if}

    {#if sections && sections.length > 0}
      {#each sections.sortBy((x) => x.position) as section (section.id)}
        <ColumnSection
          id={section.id}
          mobile={true}
          scrollPastRecId={scrollPastRecId}
          bind:showAddComment={showAddComment} />
      {/each}
    {:else if recs && recs.length == 0}
      <NoRecsFoundText mobile={true} currentUser={currentUser} group={group} />
    {:else}
      <ColumnSection mobile={true} scrollPastRecId={scrollPastRecId} bind:showAddComment={showAddComment} />
    {/if}

    {#if userCanContribute}
      <FixedAddButton
        group={group}
        sticky={true}
        mobile={true}
        withQuiz={withQuiz}
        withMobileFooter={true}
        on:clicked={(e) => {
          dispatchAddButtonClicked(e);
        }} />
    {/if}
    {#if shouldShowMapViewButton}
      {#key showAddComment}
        <MobileGoToMapButton
          withAddComment={showAddComment}
          withMobileFooter={isSignedInUser}
          contributorView={userCanContribute}
          on:click={dispatchMapViewButtonClicked} />
      {/key}
    {/if}

    <br />
    <br />
  {/if}
</div>
{#if group.permissions.edit && browser}
  <Quiz
    bind:this={quizInstance}
    mobile={true}
    group={group}
    bind:withQuiz={withQuiz}
    bind:expanded={quizExpanded}
    loadSections={() => loadSections()}
    on:clicked={(e) => {
      dispatch('add-button-click', {
        question: e.detail.question,
        quizExpanded: quizExpanded,
        quizInstance: quizInstance,
      });
    }} />
{/if}

<div style="height: 200px" />

{#if browser && BrowserUtils.isIOS()}
  <input type="text" id="mobile-grid-focus-input" style="opacity: 0; height: 0px;" />
{/if}
